/** @jsxImportSource @emotion/react */
import { ConfirmActionModal } from "../../../components/ConfirmActionModal";
import { ButtonAction } from "../../../components/NewButtons";
import { FormProvider, useForm } from "react-hook-form";
import { useGetShops } from "../../shop/service/shop-api";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { StyledForm } from "../../../components/StyledForm";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import { SpinnerBlock } from "../../common/Spinner";
import {
  useCreateMenu,
  usePublishMenuDraft,
  useUpdateMenuDraftProducts,
} from "../service/menus-api";
import { shopHttp } from "../../shop/service/shop-http";
import { useNavigate } from "react-router";
import { routes } from "../../../appRoutingHelpers";
import { toastr } from "react-redux-toastr";
import { productsHttp } from "../../products/service/products-http";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { ActionSpinner } from "../../../components/ActionSpinner";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  setMenuGenerationIsLoading: (value: boolean) => void;
};

export const CreateMenuFromShopModal = ({
  isOpen,
  onClose,
  setMenuGenerationIsLoading,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const methods = useForm<{ id: string }>({
    defaultValues: {
      id: "",
    },
  });
  const navigate = useNavigate();
  const { mutateAsync: createMenu } = useCreateMenu();
  const { mutateAsync: updateMenuDraftProducts } = useUpdateMenuDraftProducts();
  const { mutateAsync: publishMenu } = usePublishMenuDraft();
  const { data, isLoading, error } = useGetShops(organizationKeyName);

  const generateMenu = async (values: { id: string }) => {
    setMenuGenerationIsLoading(true);
    onClose();
    try {
      // Retrieve shop to get country code
      const shop = await shopHttp.getShop(organizationKeyName, values.id);

      // Getting current products
      const products = await productsHttp.getShopProductList(
        organizationKeyName,
        {
          shopId: values.id,
          classification: "admin-main-list",
        }
      );

      if (products && products.length === 0) {
        toastr.info(
          "Restaurant has no current products",
          "If this restaurant is already using menus, you cannot generate a new menu from products on the restaurant."
        );
        return;
      }

      const firstProduct = products.find((product) => !!product.price);

      if (!firstProduct) {
        toastr.info(
          "Error",
          "This restaurant has no products with a price. Please contact support."
        );
        return;
      }

      // Create an identical menu
      const id = await createMenu({
        data: {
          countryCode: shop.countryCode,
          internalNote: "",
          name: `Menu with products from ${shop.name}`,
          isMenuOverwritesEnabled: false,
          currency: firstProduct.price?.currency!,
        },
        organizationKeyName,
      });

      // Update menu and save as draft
      await updateMenuDraftProducts({
        data: {
          products: products.map((product, i) => ({
            id: product.id,
            sortKey: i,
          })),
        },
        id,
        organizationKeyName,
      });

      // Publishing menu
      await publishMenu({
        id,
        organizationKeyName,
      });
      navigate(routes.menuEdit(organizationKeyName, id));
      toastr.success("Success", "Menu generated successfully");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    } finally {
      setMenuGenerationIsLoading(false);
    }
  };

  useHandleFetchError(error);

  return (
    <FormProvider {...methods}>
      <ConfirmActionModal
        isOpen={isOpen}
        onClose={onClose}
        width={600}
        cancelButtonText="Cancel"
        title="Create menu from restaurant products"
        subtitle="Ease migration to menus by creating a menu with the same products that are currently on a specific restaurant."
        confirmButton={
          <ButtonAction
            onClick={methods.handleSubmit(generateMenu)}
            type="button"
            disabled={methods.formState.isSubmitting}
          >
            {methods.formState.isSubmitting ? <ActionSpinner /> : "Create menu"}
          </ButtonAction>
        }
      >
        <StyledForm>
          {isLoading ? (
            <SpinnerBlock />
          ) : (
            <SelectInput
              name="id"
              label="Restaurant"
              helperText="Choose restaurant to create menu from"
              options={(data || []).map((shop) => ({
                label: shop.name,
                value: shop.id,
              }))}
            />
          )}
        </StyledForm>
      </ConfirmActionModal>
    </FormProvider>
  );
};
